import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import Cookies from 'js-cookie'

export const paramsToMap = [
  { param: 'area', legacyParam: 'Area' },
  { param: 'Area', legacyParam: 'Area' },
  { param: 'flag', legacyParam: 'Flag' },
  { param: 'Flag', legacyParam: 'Flag' },
  { param: 'clientNo', legacyParam: 'ClientNo' },
  { param: 'matterNo', legacyParam: 'MatterNo' },
  { param: 'clientMatterNo', legacyParam: 'MatterNo' },
  { param: 'searchCriteria', legacyParam: 'searchCriteria' },
  { param: 'isMatterHistorySearch', legacyParam: 'isMHSearch' },
  { param: 'isMHSearch', legacyParam: 'isMHSearch' },
  { param: 'RedirectPath', legacyParam: 'RedirectPath' },
  { param: 'returnUrl', legacyParam: 'returnurl' },
  { param: 'redirectTo', legacyParam: 'redirectTo' }
]

const getUrlQueryParams = () => {
  // add any url query params and route params to the iframe src if necessary
  let urlQueryParams = {} // these are the query params from the current URL
  const { search, hash } = window.location
  let searchArgument

  if (search) searchArgument = search
  else if (hash?.includes('?')) {
    searchArgument = hash.substring(hash.indexOf('?'))
  }

  if (!searchArgument) return urlQueryParams

  const urlSearchParams = new URLSearchParams(searchArgument)

  urlSearchParams?.forEach((value, key) => {
    // add the query params
    urlQueryParams[key] = value.trim()
  })

  return urlQueryParams
}

export const getRouteAndQueryParamsToAddToIframeSrc = ({ params }) => {
  paramsToMap.forEach(({ param, legacyParam }) => {
    if (Object.keys(params).includes(param)) {
      const tempValue = params[param]
      delete params[param]

      if (param === 'clientMatterNo') {
        const { clientNo, matterNo } =
          useUrlStore.getState().urlDecodeClientMatterNo({ clientMatterNoParam: tempValue }) || {}
        params['ClientNo'] = clientNo
        params['MatterNo'] = matterNo
      } else params[legacyParam] = tempValue
    }
  })

  if (!Object.keys(params)?.length) return ''

  let paramArray = []
  Object.keys(params).forEach((key) => {
    let value = params[key]

    if (value && typeof value === 'string') {
      value = encodeURIComponent(value)
      value = value.replace(/ /g, '+').replace(/%20/g, '+').replace(/%2B/g, '+')
    }

    paramArray.push(`${key}=${value}`)
  })
  return paramArray
}

const getIframeWidth = () => {
  const { sidebarMode } = useGlobalSettingsStore.getState()
  const offset = sidebarMode === 'expanded' ? 404 : 204
  return document.body.scrollWidth - offset
}

const getIframeHeight = () => {
  return document.body.scrollHeight - 320
}

export const scrollToTop = (contentWindow) => {
  // scroll to top on navigation
  if (contentWindow?.document?.body) contentWindow.document.body.scrollTop = 0
  document.body.scrollTop = 0
  document.querySelector('#reactroot').scrollTop = 0
}

const configureOsprey1PopupSettings = () => {
  // set localstorage settings, to control where Osprey 1 places email and SMS popups
  const iframeWidth = getIframeWidth()
  const iframeHeight = getIframeHeight()
  const popupWidth = iframeWidth < 800 ? 620 : 800
  const popupHeight = iframeHeight < 760 ? 550 : 700

  const popupSettings = {
    weight: null,
    width: popupWidth,
    height: popupHeight,
    collapsed: false,
    left: (iframeWidth - popupWidth) / 2, // center the popup
    top: 60,
    maximized: false
  }

  const stringifiedPopupSettings = JSON.stringify(popupSettings)

  // these settings will be used by Osprey 1 to position the popups
  localStorage.setItem('matter-history-send-sms', stringifiedPopupSettings)
  localStorage.setItem('matter-history-send-email', stringifiedPopupSettings)
  return stringifiedPopupSettings
}

export const triggerPostLoadAction = (
  postLoadAction,
  getContentWindow,
  isLocal,
  postLoadActionShouldWaitForIframeToLoad,
  attempt = 0,
  targetAspxPage = 'matterpage.aspx',
  selector = '#pra-send-email-client'
) => {
  if (isLocal) return

  if (postLoadAction)
    console.warn({ postLoadAction, targetAspxPage, selector, postLoadActionShouldWaitForIframeToLoad })
  const { resetPostLoadAction } = useUrlStore.getState()

  if (!postLoadAction || !selector) {
    resetPostLoadAction()
    throw 'Unable to process post load action'
  }

  // if we have tried 5 times, give up
  if (attempt > 5) {
    console.error(`Failed to trigger post load action after ${attempt} attempts`)
    resetPostLoadAction()
    return
  }

  // try to execute the action, if the dispatcher is not available, wait for it to be available
  const contentWindow = getContentWindow()
  const postLoadSelector = typeof selector === 'string' ? selector : typeof selector === 'function' ? selector() : null
  const elementToClick = contentWindow?.document?.querySelector(postLoadSelector)

  // wait for the iframe to be on the target page and for the dom element to be ready/found
  if (contentWindow?.location?.href?.toLowerCase().includes(targetAspxPage) && elementToClick) {
    configureOsprey1PopupSettings()

    // add a delay to allow the iframe to load
    setTimeout(() => {
      elementToClick.click()
      scrollToTop(contentWindow)
      resetPostLoadAction()
    }, 500)
  } else {
    // document not ready yet, try again in 500ms
    /* console.log({
      contentWindow,
      href: contentWindow?.location?.href,
      attempt,
      postLoadAction,
      postLoadActionShouldWaitForIframeToLoad,
      targetAspxPage,
      selector
    }) */
    setTimeout(() => {
      triggerPostLoadAction(
        postLoadAction,
        getContentWindow,
        isLocal,
        postLoadActionShouldWaitForIframeToLoad,
        attempt + 1,
        targetAspxPage,
        selector
      )
    }, 500)
  }
}

export const getIframeLocation = ({ iframeURL, databaseSuffix }) => {
  return {
    pathname: iframeURL.pathname.replace(`/${databaseSuffix}/`, ''),
    search: iframeURL.search,
    host: iframeURL.host
  }
}

export const getIframePreviousPageParams = ({ location, newIframeSrc }) => {
  // if the iframe has navigated internally, we need to get the query params so they are applied when the iframe is reloaded
  const iframePreviousLocation = location?.state?.iframeLocation
  const { pathname, search } = iframePreviousLocation || {}

  if (pathname && search && newIframeSrc.includes(pathname)) {
    const iframeParams = {}
    search
      .replace('?', '')
      .split('&')
      .forEach((paramPair) => {
        const [key, value] = paramPair.split('=')
        iframeParams[key] = value.trim()
      })

    return iframeParams
  }

  return null
}

export const getIframeParams = ({ iframeURL }) => {
  let iframeParams = {}

  iframeURL.search
    .replace('?', '')
    .split('&')
    .forEach((param) => {
      const [key, value] = param.split('=')
      iframeParams[key.toLowerCase()] = value?.trim() || ''
    })

  // now add any params that are in the React url query string
  const urlQueryParams = getUrlQueryParams()
  if (Object.keys(urlQueryParams).length) console.log({ urlQueryParams })
  // iframeParams = { ...iframeParams, ...urlQueryParams }

  const menuItemId = iframeParams.flag || iframeParams.Flag
  const menuItemAreaId = iframeParams.area || iframeParams.Area

  // now remove any params that are not needed, because they are handled elsewhere
  delete iframeParams.flag
  delete iframeParams.Flag
  delete iframeParams.area
  delete iframeParams.Area
  delete iframeParams.clientNo
  delete iframeParams.clientno
  delete iframeParams.ClientNo
  delete iframeParams.matterNo
  delete iframeParams.matterno
  delete iframeParams.MatterNo

  Object.keys(iframeParams).forEach((key) => {
    // false and '' are valid values, so we need to check for null or undefined
    if (!key || iframeParams[key] === null || iframeParams[key] === undefined) delete iframeParams[key]
  })

  return { iframeParams, menuItemId, menuItemAreaId }
}

export const getClientNoAndMatterNoFromCookies = () => {
  const lastClientMatterCookie = Cookies.get('LastCliMatt')
  let clientNo
  let matterNo

  if (lastClientMatterCookie) {
    lastClientMatterCookie.split('&').forEach((detail) => {
      const [key, value] = detail.split('=')
      if (key === 'clientNo') {
        clientNo = value
      }

      if (key === 'matterNo') {
        matterNo = value
      }
    })
  }

  return { clientNo, matterNo }
}

export const lookupRouteToMatchIframeContent = ({
  menuItemId,
  menuItemAreaId,
  iframePath,
  iframeSearchParams,
  allRoutesByMenuItemId,
  routesByAspxPageName
}) => {
  const lowerIframePath = iframePath?.toLowerCase() || ''
  const lowerIframePathWithoutReportPrefix = lowerIframePath.replace('reportlib/', '')
  const lowerIframeSearchParams = iframeSearchParams?.toLowerCase() || ''
  let candidates = []
  let candidateIds = []

  // first add any matches on menuItemId
  if (menuItemId && allRoutesByMenuItemId[menuItemId]) {
    candidates = [...allRoutesByMenuItemId[menuItemId]]
    // keep track of the ids so we don't add duplicates later
    candidateIds = candidates.map((candidate) => candidate.id)
  }

  // then add any matches on aspxPageName, making sure that the new entries are not already in the id array
  const routesByPage = routesByAspxPageName[lowerIframePathWithoutReportPrefix]
  if (lowerIframePath && routesByPage) {
    routesByPage.forEach((candidate) => {
      const candidateAlreadyAdded = candidateIds.includes(candidate.id)
      if (!candidateAlreadyAdded) {
        candidates.push(candidate)
      }
    })
  }

  if (!candidates || !candidates.length) {
    return ''
  }

  if (candidates.length === 1) {
    return candidates[0].path
  }

  // when we have multiple candidates, score them on the basis of matching criteria to find the best match
  const candidateScores = candidates
    .map((candidate, index) => {
      let matchScore = 0

      if (candidate?.redirectTo) matchScore -= 1000 // massively down-grade redirects, as we don't want those

      // console.log({ lowerIframePath, candidate, iframeSearchParams })
      const aspxPageNameMatch = candidate?.aspxPageName?.toLowerCase() === lowerIframePathWithoutReportPrefix
      const menuItemIdMatch = Number(candidate?.menuItemId) === Number(menuItemId)
      const menuItemAreaIdMatch = Number(candidate?.menuItemAreaId) === Number(menuItemAreaId)
      const lowerIframeSearchParams = iframeSearchParams.toLowerCase() || ''

      const routeParamMatchClientMatterNo =
        candidate?.id?.includes(':clientMatterNo') &&
        lowerIframeSearchParams?.includes('clientno') &&
        lowerIframeSearchParams?.includes('matterno')

      const routeParamMatchClientNo =
        candidate?.id?.includes(':clientNo') && lowerIframeSearchParams.includes('clientno')

      // the best case scenario is that the menuItemId, menuItemAreaId AND aspxPageName match (score: 19)
      // after that, the next best is that the aspxPageName and menuItemId matches (score: 15)
      // next, it is aspxPageName matches, with menuItemAreaId (score: 14)
      // next, it is menuItemAreaId and menuItemId match (score: 9)
      // next, menuItemId matches, but aspxPageName and menuItemAreaId are different (score: 5)
      // finally, menuItemAreaId matches, but aspxPageName and menuItemId are different (score: 4)
      // so each item has a weighted score, and we add them up to get the best match
      if (aspxPageNameMatch) matchScore += 10
      if (menuItemIdMatch) matchScore += 5
      if (menuItemAreaIdMatch) matchScore += 4
      if (routeParamMatchClientMatterNo || routeParamMatchClientNo) matchScore += 1

      // if we have options where the menuItemId and aspxPageName both match, we can add/remove a modifier if the aspxUrlParams match
      // This is great when we have multiple routes with the same aspxPageName and the menuItemId doesn't provide a clear winner
      const { aspxUrlParams, id, aspxPageName } = candidate

      if (aspxUrlParams?.length) {
        aspxUrlParams.forEach(({ key, value }) => {
          let paramString = `${key}=${value === '*' ? '' : value}`
          paramString = paramString ? paramString.toLowerCase() : ''
          const searchParamMatch = paramString && lowerIframeSearchParams.includes(paramString)

          if (searchParamMatch) matchScore += 3
          else matchScore -= 1
        })
      }

      // if in cases where we still have candidates with the same score, we can add a modifier based on the delta in menuItemId
      // this is useful because sometimes Osprey1 applies the wrong menuItemId to a route, because it uses the parent menuItemId.
      // by comparing the delta menuItemId, we can see which is the closest match
      const menuItemIdDelta = Math.abs(Number(candidate.menuItemId) - Number(menuItemId))

      return {
        index,
        matchScore,
        aspxPageNameMatch,
        id,
        menuItemId: candidate.menuItemId,
        aspxPageName,
        aspxUrlParams,
        menuItemIdDelta
      }
    })
    .sort((a, b) => {
      // OS-2715 round 3: an exact aspxPageName match must always outrank a candidate
      // without one, regardless of aspxUrlParams-derived score -- otherwise a sibling
      // route sharing this menuItemId can out-score a genuine page match purely from
      // generic Area/Flag values every route on this menuItemId carries (analysis.md's
      // "asymmetry"). This tier check runs before matchScore so that case can never win.
      if (a.aspxPageNameMatch !== b.aspxPageNameMatch) return a.aspxPageNameMatch ? -1 : 1

      if (a.matchScore < b.matchScore) return 1
      if (a.matchScore > b.matchScore) return -1

      // i.e. matchScore is equal and we need to tie-break based on nearest menuItemId
      if (a.menuItemIdDelta > b.menuItemIdDelta) return 1
      if (a.menuItemIdDelta < b.menuItemIdDelta) return -1

      return 0
    })

  console.warn({ candidates, candidateScores, menuItemId, lowerIframePath, lowerIframeSearchParams })
  const bestCandidateIndex = candidateScores?.length ? candidateScores[0].index : null
  return candidates[bestCandidateIndex].path
}

export const replaceMatchingRouteParams = ({ matchingRoute, clientNo, matterNo }) => {
  const clientMatterNo = useUrlStore.getState().urlEncodeClientMatterNo({ clientNo, matterNo })

  let routeWithParams = matchingRoute?.replace(':clientMatterNo', clientMatterNo)

  // ensure there are no double slashes, in cases where clientNo and matterNo are empty
  routeWithParams = routeWithParams?.replace(/\/\//g, '/')

  // remove ? if last character
  if (routeWithParams?.endsWith('?')) {
    routeWithParams = routeWithParams.slice(0, -1)
  }

  return routeWithParams
}
